/**
 * Returns the calendar date (YYYY-MM-DD) for "now" in the given IANA timezone.
 * Falls back to UTC when timezone is missing or invalid, so callers always get a value.
 */
export function todayInTimezone(timezone?: string | null): string {
  const utcToday = () => new Date().toISOString().split('T')[0];

  if (!timezone) {
    return utcToday();
  }

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date());

    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    if (!year || !month || !day) {
      return utcToday();
    }

    return `${year}-${month}-${day}`;
  } catch {
    return utcToday();
  }
}

/**
 * Returns the last calendar day of the current month in the given IANA timezone
 * as YYYY-MM-DD. Falls back to UTC when timezone is missing or invalid.
 */
export function endOfMonthInTimezone(timezone?: string | null): string {
  const today = todayInTimezone(timezone);
  const [year, month] = today.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}
